import React, { useState } from "react";
import { Check, Lock, CreditCard, Zap } from "lucide-react";
import { showToast } from "../../../../../Helper/Toast";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import readyToGoIcon from "../../../../../assets/img/ready to go.svg";
import {stripeKey, ApiBaseUrl} from "../../../../../Utils/Constants";


const Credits = () => {
  const navigation = useNavigate();
  const dispatch = useDispatch();

  const stripePromise = loadStripe(
    stripeKey
  );

  const handleCheckout = async () => {
    //console.log("handleCheckout called", selectedCredits);
    const stripe = await stripePromise;

    const response = await axios.post(
      `${ApiBaseUrl}v1/user/createStripeSessionNew`,
      {
        amount: selectedCredits.amount * 100, // Amount in cents
        currency: "usd",
        // You can add more details here as needed
      },
      {
        headers: {
          Authorization: `${localStorage.getItem("clientToken")}`,
        },
      }
    );
    if (response.data.status === true) {
      window.location.href = response.data.data.url; // Redirect to Stripe Checkout
    } else {
      showToast(response.data.data.message, "error");
    }
  };

  const [selectedCredits, setSelectedCredits] = useState({
    amount: 25,
    price: 25,
  });
  const [customAmount, setCustomAmount] = useState("");

  const creditOptions = [
    { credits: 10, price: 10, popular: false },
    { credits: 25, price: 25, popular: true },
    { credits: 50, price: 50, popular: false },
    { credits: 100, price: 100, popular: false },
  ];

  const benefits = [
    "Credits never expire while account is active",
    "1 credit = $1.00 (no hidden fees)",
    "Instant credit delivery",
    "Secure payment processing",
  ];

  const visibilityTiers = [
    {
      badge: "w/ Camera Measurement",
      badgeColor: "#9333ea",
      icon: (
        <img
          src={readyToGoIcon}
          alt="ready-to-go-icon"
          style={{ width: "24px", height: "24px" }}
        />
      ),
      title: "Ready-to-Go (Camera)",
      credits: 5,
      description: "Camera scanned job posting — more accurate bids.",
    },
    {
      badge: "w/ Manual Measurement",
      badgeColor: "#9333ea",
      icon: (
        <img
          src={readyToGoIcon}
          alt="ready-to-go-icon"
          style={{ width: "24px", height: "24px" }}
        />
      ),
      title: "Ready-to-Go (Manual)",
      credits: 10,
      description: "Manually measurement entries — may produce less bids.",
    },
    {
      badge: "w/ Manual Measurement",
      badgeColor: "#9333ea",
      icon: (
        <img
          src={readyToGoIcon}
          alt="ready-to-go-icon"
          style={{ width: "24px", height: "24px" }}
        />
      ),
      title: "Ready-to-Go (Manual)",
      credits: 20,
      description:
        "No measurements in job post — bids may be less frequent and less accurate.",
    },
  ];

  const handleCustomAmount = (value) => {
    setCustomAmount(value);
    if (value && parseInt(value) >= 5) {
      setSelectedCredits({ amount: parseInt(value), price: parseInt(value) });
    }
  };

  return (
    <>
      <div className="credits-purchase-container">
        {/* Header */}
        <div className="page-header">
          <h1>Credits</h1>
          <h2>Purchase Credits</h2>
          <p className="page-description">
            Use credits to post Ready-to-Go jobs.
            <br />
            Each credit equals $1.00 and helps you reach more verified
            professionals.
          </p>
        </div>

        {/* Main Content */}
        <div className="main-content">
          {/* Left Column - Credit Selection */}
          <div className="credit-selection">
            <h3 className="section-title">Select Credit Amount</h3>

            <div className="credit-options-grid">
              {creditOptions.map((option) => (
                <div
                  key={option.credits}
                  className={`credit-card ${option.popular ? "popular" : ""} ${
                    selectedCredits.amount === option.credits && !customAmount
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => {
                    setSelectedCredits({
                      amount: option.credits,
                      price: option.price,
                    });
                    setCustomAmount("");
                  }}
                >
                  {option.popular && (
                    <div className="popular-badge">Popular</div>
                  )}
                  <div className="credit-amount">{option.credits}</div>
                  <div className="credit-label">credits</div>
                  <div className="credit-price">${option.price}</div>
                </div>
              ))}
            </div>

            {/* Custom Amount */}
            <div className="custom-amount-section">
              <p className="custom-amount-label">
                <strong>Or enter custom amount (minimum 5 credits)</strong>
              </p>
              <input
                type="number"
                className="custom-input"
                placeholder="Enter credit amount"
                value={customAmount}
                onChange={(e) => handleCustomAmount(e.target.value)}
                min="5"
              />
              {customAmount && parseInt(customAmount) >= 5 && (
                <div className="purchase-summary">
                  You're purchasing {customAmount} credits for ${customAmount}
                  .00
                </div>
              )}
            </div>
          </div>

          {/* Right Column - What You Get */}
          <div className="what-you-get">
            <div className="benefits-list">
              <h3>What You Get</h3>
              {benefits.map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <Check size={18} />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            <div className="security-box">
              <div className="security-header">
                <Lock size={18} />
                <h4>Secure Payment</h4>
              </div>
              <p className="security-text">
                Your payment is protected by industry-standard encryption. We
                accept all major credit cards and process payments securely.
              </p>
            </div>

            <button
              className="purchase-button"
              onClick={() => {
                handleCheckout();
              }}
            >
              <CreditCard size={18} />
              <span>
                Purchase {selectedCredits.amount} Credits - $
                {selectedCredits.price}.00
              </span>
            </button>
          </div>
        </div>

        {/* Visibility Tiers Section */}
        <div className="visibility-section">
          <div className="visibility-header">
            <h2>
              Ready to Go Costs{" "}
              <span className="highlight">(Boosted Visibility)</span>
            </h2>
          </div>

          <div className="visibility-cards">
            {visibilityTiers.map((tier, index) => (
              <div key={index} className="visibility-card">
                <div className="tier-badge">{tier.badge}</div>
                <div className="tier-title-row">
                  <span className="tier-icon">{tier.icon}</span>
                  <h4 className="tier-title">{tier.title}</h4>
                </div>
                <div className="tier-credits">{tier.credits} credits</div>
                <p className="tier-description">{tier.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Credits;
